import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, ROLE } from '../lib/auth';
import { useClients } from '../lib/lookups';

/**
 * Artwork Register — the design team's "Artwork No. Detail" sheet, in the
 * system.  Anyone in the team books, edits and finishes any entry.  An
 * artwork that came straight to design is booked here before its PO exists;
 * "Link PO" attaches the PO when it is punched.  API: /api/artworks.
 */

interface Artwork {
  id: number; artworkNo: string; artDate: string;
  clientId: string | null; clientName: string; clientCode: string | null;
  jobName: string; cartonSize: string | null; sheetSize: string | null; ups: string | null;
  dieNo: string | null; cartonType?: string | null; status: string; designerId: number | null; designerName: string | null;
  remark: string | null; poId: string | null; poNumber: string | null; source: string;
  createdByName: string | null; createdOn: string;
}
interface PoHit {
  poId: string; poNumber: string | null; jobName: string | null; clientId: string | null; client: string | null;
  materialNo: string | null; poDate: string | null; orderType: string | null; artworkNo: string | null;
}
type Draft = {
  artworkNo: string; artDate: string; clientId: string; clientName: string; clientCode: string; jobName: string;
  // Carton L × W × H and the sheet cut size L × W, all in mm.
  cartonL: string; cartonW: string; cartonH: string; cutL: string; cutW: string;
  // What an older entry had that is not in mm numbers (e.g. 26''x24" from the sheet), kept unless replaced.
  oldCarton: string; oldSheet: string;
  ups: string; dieNo: string; cartonType: string; status: string;
  designerId: string; remark: string; poId: string; poLabel: string;
};
interface DieHit {
  source: string; dieNo: string; designNo: string | null; ups: string | null; productName: string | null; client: string | null;
  cartonType?: string | null;
  l: string | null; w: string | null; h: string | null; cutL: string | null; cutW: string | null; upsMatch: number;
}
/** "80X62X87 mm" → ['80','62','87']; anything else → null. */
function parseCarton(s: string | null): [string, string, string] | null {
  const m = /^\s*(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*(mm)?\s*$/i.exec(s ?? '');
  return m ? [m[1]!, m[2]!, m[3]!] : null;
}
/** "530 x 760 mm" → ['530','760']; inches or anything else → null. */
function parseCut(s: string | null): [string, string] | null {
  const m = /^\s*(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*mm\s*$/i.exec(s ?? '');
  return m ? [m[1]!, m[2]!] : null;
}
const num = (v: string | null | undefined) => (v ?? '').replace(/[^0-9.]/g, '');

const today = () => new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);
const blank = (): Draft => ({
  artworkNo: '', artDate: today(), clientId: '', clientName: '', clientCode: '', jobName: '',
  cartonL: '', cartonW: '', cartonH: '', cutL: '', cutW: '', oldCarton: '', oldSheet: '',
  ups: '', dieNo: '', cartonType: '', status: 'In Progress', designerId: '', remark: '', poId: '', poLabel: '',
});
const fmtDate = (d: string) => {
  const [y, m, dd] = d.split('-');
  return y && m && dd ? `${dd}.${m}.${y}` : d;
};
const ERR: Record<string, string> = {
  artwork_register_not_available: 'The Artwork Register needs the latest database update — ask the admin to run the deploy.',
  po_not_found: 'That PO was not found.',
  super_admin_only: 'Only Super Admin can delete an entry.',
};
const friendly = (e: unknown) => {
  const m = (e as Error)?.message ?? '';
  const taken = /^po_has_artwork:(.+)$/.exec(m);
  if (taken) return `That PO already has artwork ${taken[1]}.`;
  const noTaken = /^artwork_no_taken:(.+)$/.exec(m);
  if (noTaken) return `Artwork No. ${noTaken[1]} is already used by another artwork.`;
  if (/invalid|expected string/i.test(m) && /artworkNo/.test(m)) return 'The Artwork No. can have only letters, numbers and / - _ . (up to 30).';
  return ERR[m] ?? m;
};

function statusClass(s: string): string {
  const u = s.toUpperCase();
  if (u === 'DONE') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (u === 'HOLD') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (u === 'CANCELLED') return 'bg-slate-100 text-slate-500 border-slate-200';
  return 'bg-orange-50 text-orange-700 border-orange-200';
}

export function ArtworksPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [status, setStatus] = useState('');
  const [month, setMonth] = useState('');
  const [noPo, setNoPo] = useState(false);
  const [page, setPage] = useState(1);
  const [form, setForm] = useState<{ id: number | null; d: Draft } | null>(null);
  const [linkFor, setLinkFor] = useState<Artwork | null>(null);

  const meta = useQuery({
    queryKey: ['artwork-meta'],
    queryFn: () => api<{ statuses: string[]; designers: { id: number; name: string }[] }>('/api/artworks/meta'),
    staleTime: 5 * 60_000,
  });
  const list = useQuery({
    queryKey: ['artworks', search, status, month, noPo, page],
    queryFn: () => {
      const sp = new URLSearchParams({ page: String(page), pageSize: '50' });
      if (search) sp.set('q', search);
      if (status) sp.set('status', status);
      if (month) sp.set('month', month);
      if (noPo) sp.set('noPo', '1');
      return api<{ rows: Artwork[]; total: number; unavailable?: boolean }>(`/api/artworks?${sp}`);
    },
    placeholderData: (prev) => prev,
  });
  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: ['artworks'] }),
    qc.invalidateQueries({ queryKey: ['artwork-meta'] }),
    qc.invalidateQueries({ queryKey: ['flow-queue'] }),
  ]);

  // "+ Book artwork no." from a Design Queue row: start from its PO.
  const fromPo = params.get('fromPo');
  useEffect(() => {
    if (!fromPo) return;
    api<Record<string, string | null>>(`/api/artworks/from-po/${encodeURIComponent(fromPo)}`)
      .then((r) => setForm({ id: null, d: {
        ...blank(), clientId: r.clientId ?? '', clientName: r.clientName ?? '', clientCode: r.clientCode ?? '',
        jobName: r.jobName ?? '', cartonL: num(r.cartonL), cartonW: num(r.cartonW), cartonH: num(r.cartonH),
        cutL: num(r.cutL), cutW: num(r.cutW), dieNo: r.dieNo ?? '', cartonType: r.cartonType ?? '', ups: num(r.ups),
        poId: r.poId ?? '', poLabel: `${r.poNumber || r.poId}`,
        designerId: user?.rollId === ROLE.Designers ? String(user.userId) : '',
      } }))
      .catch(() => {})
      .finally(() => { params.delete('fromPo'); setParams(params, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPo]);

  const setStatusQuick = useMutation({
    mutationFn: (v: { id: number; status: string }) =>
      api(`/api/artworks/${v.id}/status`, { method: 'PUT', body: JSON.stringify({ status: v.status }) }),
    onSuccess: refresh,
    onError: (e) => alert(friendly(e)),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api(`/api/artworks/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => alert(friendly(e)),
  });

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / 50));
  const isSuperAdmin = user?.rollId === ROLE.SuperAdmin;

  const openNew = () => setForm({ id: null, d: { ...blank(), designerId: user?.rollId === ROLE.Designers ? String(user.userId) : '' } });
  const openEdit = (a: Artwork) => {
    const c = parseCarton(a.cartonSize);
    const k = parseCut(a.sheetSize);
    setForm({ id: a.id, d: {
    artworkNo: a.artworkNo, artDate: a.artDate, clientId: a.clientId ?? '', clientName: a.clientName, clientCode: a.clientCode ?? '',
    jobName: a.jobName,
    cartonL: c?.[0] ?? '', cartonW: c?.[1] ?? '', cartonH: c?.[2] ?? '', oldCarton: c ? '' : (a.cartonSize ?? ''),
    cutL: k?.[0] ?? '', cutW: k?.[1] ?? '', oldSheet: k ? '' : (a.sheetSize ?? ''),
    ups: a.ups ?? '',
    dieNo: a.dieNo ?? '', cartonType: a.cartonType ?? '', status: a.status, designerId: a.designerId ? String(a.designerId) : '',
    remark: a.remark ?? '', poId: a.poId ?? '', poLabel: a.poNumber || a.poId || '',
  } });
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-brand">Artwork Register</h1>
          <p className="max-w-2xl text-sm text-slate-500">
            Every artwork the design team makes, with its Artwork No. Book one here when a job comes straight to design;
            link its PO when it is punched.
          </p>
        </div>
        <button className="btn-primary" onClick={openNew}>+ Book artwork</button>
      </div>

      {list.data?.unavailable && (
        <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {ERR.artwork_register_not_available}
        </div>
      )}

      <form className="mb-4 flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); setPage(1); }}>
        <input className="input max-w-xs" placeholder="Artwork no., client, job, die, PO, or size 68 x 53 x 67…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn-primary" type="submit">Search</button>
        <select className="input w-40" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="open">Not done yet</option>
          {(meta.data?.statuses ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input className="input w-40" type="month" value={month} onChange={(e) => { setMonth(e.target.value); setPage(1); }} title="Month" />
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={noPo} onChange={(e) => { setNoPo(e.target.checked); setPage(1); }} /> No PO yet
        </label>
        {(search || status || month || noPo) && (
          <button type="button" className="btn-ghost" onClick={() => { setQ(''); setSearch(''); setStatus(''); setMonth(''); setNoPo(false); setPage(1); }}>Reset</button>
        )}
      </form>

      <div className="card overflow-hidden">
        <div className="card-header flex justify-between">
          <span>Artwork No. Detail</span>
          <span className="text-xs font-normal">{total} artwork{total === 1 ? '' : 's'}{pages > 1 ? ` · page ${page} of ${pages}` : ''}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs [&_td]:border-t [&_td]:border-slate-100">
            <thead className="bg-slate-100 text-left text-[11px] uppercase text-slate-600">
              <tr>
                {/* Related columns share a cell, so the register fits a laptop screen. */}
                <th className="px-2 py-2" title="The Design No. in Product Master">Artwork No. · Date</th>
                <th className="px-2 py-2">Client · Code</th>
                <th className="px-2 py-2">Job Name</th>
                <th className="px-2 py-2">Carton · Cut / Ups · Die · Type</th>
                <th className="px-2 py-2">Status</th>
                <th className="px-2 py-2">Acx</th>
                <th className="px-2 py-2">Remark</th>
                <th className="px-2 py-2">PO</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>}
              {list.data && rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">No artworks{search || status || month || noPo ? ' match' : ' yet — book the first one'}.</td></tr>
              )}
              {rows.map((a) => (
                <tr key={a.id} className="align-top hover:bg-slate-50">
                  <td className="whitespace-nowrap px-2 py-1.5">
                    <div className="font-mono font-semibold text-slate-800">{a.artworkNo}</div>
                    <div className="text-[11px] text-slate-500">{fmtDate(a.artDate)}</div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div>{a.clientName}</div>
                    {a.clientCode && <div className="font-mono text-[11px] text-slate-500">{a.clientCode}</div>}
                  </td>
                  <td className="px-2 py-1.5 font-medium text-slate-800">{a.jobName}</td>
                  <td className="px-2 py-1.5">
                    {a.cartonSize && <div className="whitespace-nowrap">{a.cartonSize}</div>}
                    {(a.sheetSize || a.ups) && (
                      <div className="whitespace-nowrap text-slate-600">{a.sheetSize}{a.ups ? <span className="text-slate-500"> = {a.ups} ups</span> : null}</div>
                    )}
                    {(a.dieNo || a.cartonType) && (
                      <div className="text-slate-500">{a.dieNo ? `Die ${a.dieNo}` : ''}{a.dieNo && a.cartonType ? ' · ' : ''}{a.cartonType ?? ''}</div>
                    )}
                    {!a.cartonSize && !a.sheetSize && !a.ups && !a.dieNo && !a.cartonType && <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    <select value={a.status} title="Change status"
                            onChange={(e) => setStatusQuick.mutate({ id: a.id, status: e.target.value })}
                            className={`rounded border px-1 py-0.5 text-[11px] font-semibold ${statusClass(a.status)}`}>
                      {[...new Set([a.status, ...(meta.data?.statuses ?? [])])].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5">{a.designerName ?? <span className="text-slate-400">—</span>}</td>
                  <td className="px-2 py-1.5 text-slate-600">{a.remark}</td>
                  <td className="px-2 py-1.5">
                    {a.poId
                      ? <button className="break-all text-left font-mono text-[11px] text-brand hover:underline" title="Change the linked PO" onClick={() => setLinkFor(a)}>{a.poNumber || a.poId}</button>
                      : <button className="whitespace-nowrap rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-700 hover:bg-blue-100" onClick={() => setLinkFor(a)}>Link PO</button>}
                    <div className="mt-1 whitespace-nowrap">
                      <button className="text-[11px] text-blue-600 hover:underline" onClick={() => openEdit(a)}>Edit</button>
                      {isSuperAdmin && (
                        <button className="ml-2 text-[11px] text-red-500 hover:underline"
                                onClick={() => { if (confirm(`Delete artwork ${a.artworkNo}? Its number is not reused.`)) remove.mutate(a.id); }}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 p-2 text-xs">
            <button className="btn-ghost py-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
            <span>Page {page} of {pages}</span>
            <button className="btn-ghost py-1" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next →</button>
          </div>
        )}
      </div>

      {form && (
        <ArtworkForm
          id={form.id}
          initial={form.d}
          statuses={meta.data?.statuses ?? []}
          designers={meta.data?.designers ?? []}
          onClose={() => setForm(null)}
          onSaved={async (no) => {
            setForm(null);
            await refresh();
            if (no) { setQ(no); setSearch(no); setPage(1); }
          }}
        />
      )}
      {linkFor && <LinkPoModal artwork={linkFor} onClose={() => setLinkFor(null)} onDone={async () => { setLinkFor(null); await refresh(); }} />}
    </div>
  );
}

function Modal({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className={`flex max-h-[90vh] w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} flex-col overflow-y-auto rounded-md border border-slate-200 bg-white p-4 text-sm shadow-xl`}
           onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="text-base font-semibold text-slate-800">{title}</div>
          <button className="text-lg leading-none text-slate-400 hover:text-slate-700" onClick={onClose} aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ArtworkForm({ id, initial, statuses, designers, onClose, onSaved }: {
  id: number | null; initial: Draft; statuses: string[]; designers: { id: number; name: string }[];
  onClose: () => void; onSaved: (artworkNo?: string) => void;
}) {
  const [d, setD] = useState<Draft>(initial);
  const clients = useClients();
  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setD((x) => ({ ...x, [k]: e.target.value }));
  // Numbers only (mm and ups).
  const setNum = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setD((x) => ({ ...x, [k]: e.target.value.replace(/[^0-9.]/g, '') }));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        artworkNo: d.artworkNo.trim() || undefined, artDate: d.artDate || undefined, clientId: d.clientId || null, clientName: d.clientName.trim(),
        clientCode: d.clientCode, jobName: d.jobName.trim(),
        cartonSize: d.cartonL && d.cartonW ? `${d.cartonL}X${d.cartonW}${d.cartonH ? `X${d.cartonH}` : ''} mm` : d.oldCarton,
        sheetSize: d.cutL && d.cutW ? `${d.cutL} x ${d.cutW} mm` : d.oldSheet,
        ups: d.ups, dieNo: d.dieNo, cartonType: d.cartonType, status: d.status, designerId: d.designerId ? Number(d.designerId) : null,
        remark: d.remark, poId: d.poId || null,
      };
      return id
        ? api<{ ok: boolean }>(`/api/artworks/${id}`, { method: 'PUT', body: JSON.stringify(body) }).then(() => undefined)
        : api<{ artworkNo: string }>('/api/artworks', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.artworkNo);
    },
    onSuccess: (no) => onSaved(no),
  });
  const pickClient = (name: string) => {
    const hit = (clients.data ?? []).find((c) => c.name.toLowerCase() === name.trim().toLowerCase());
    setD((x) => ({ ...x, clientName: name, clientId: hit ? hit.id : '' }));
  };
  const ok = d.clientName.trim() && d.jobName.trim();

  // Job name: products from Product Master as it is typed.  Picking one fills
  // the name (and the client and die when they are empty); not picking keeps
  // what was typed.
  const [jobOpen, setJobOpen] = useState(false);
  const jobTerm = d.jobName.trim();
  const productHits = useQuery({
    enabled: jobOpen && jobTerm.length >= 2,
    queryKey: ['artwork-product-suggest', jobTerm],
    queryFn: () => api<{ id: string; name: string; client: string | null; dieNumber: string | null }[]>(
      `/api/products/autocomplete?q=${encodeURIComponent(jobTerm)}`),
    staleTime: 30_000,
  });
  const pickProduct = (pr: { id?: string; name: string; client: string | null; dieNumber: string | null }) => {
    const cl = pr.client ? (clients.data ?? []).find((c) => c.id === pr.client) : undefined;
    setD((x) => ({
      ...x,
      jobName: pr.name,
      ...(!x.clientName.trim() && cl ? { clientName: cl.name, clientId: cl.id } : {}),
      ...(!x.dieNo.trim() && pr.dieNumber ? { dieNo: pr.dieNumber } : {}),
    }));
    setJobOpen(false);
    // Its carton type is in the full product record.
    if (pr.id) {
      void api<{ cartonType?: string | null }>(`/api/products/${encodeURIComponent(pr.id)}`)
        .then((full) => { const t = (full.cartonType ?? '').trim(); if (t) setD((x) => (x.cartonType.trim() ? x : { ...x, cartonType: t })); })
        .catch(() => undefined);
    }
  };

  return (
    <Modal title={id ? 'Edit artwork' : 'Book artwork'} onClose={onClose} wide>
      {!id && <p className="-mt-1 mb-3 text-xs text-slate-500">The Artwork No. — the product's Design No. — is given on save (year, month, 56, next number) unless you type one. A linked PO's product without a Design No. gets it.</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="label !normal-case sm:col-span-2">Artwork No.
          <input className="input font-mono" value={d.artworkNo} onChange={(e) => setD((x) => ({ ...x, artworkNo: e.target.value.replace(/\s+/g, '') }))}
                 placeholder={id ? '' : 'Leave blank for the next number'} maxLength={30} />
          {id && d.artworkNo.trim() !== initial.artworkNo && (
            <span className="text-[11px] font-normal text-amber-700">Changing it also changes the Design No. of any product that carries {initial.artworkNo}.</span>
          )}
        </label>
        <label className="label !normal-case">Date
          <input type="date" className="input" value={d.artDate} onChange={set('artDate')} />
        </label>
        <label className="label !normal-case">Client *
          <input className="input" list="artwork-clients" value={d.clientName} onChange={(e) => pickClient(e.target.value)}
                 placeholder="Pick from Client Master or type" />
          <datalist id="artwork-clients">
            {(clients.data ?? []).map((c) => <option key={c.id} value={c.name} />)}
          </datalist>
          {d.clientName && !d.clientId && <span className="text-[10px] font-normal text-amber-700">not in Client Master — kept as typed</span>}
        </label>
        <label className="label !normal-case">Client code
          <input className="input" value={d.clientCode} onChange={set('clientCode')} placeholder="e.g. AG/AR/1190/SL/CT/00" />
        </label>
        <div className="grid grid-cols-[1fr_9rem] gap-2">
          <label className="label !normal-case">Die No.
            <input className="input" value={d.dieNo} onChange={set('dieNo')} placeholder="pick from the dies suggested below" />
          </label>
          <label className="label !normal-case" title="As Product Master and the job card call it">Carton type
            <input className="input" list="artwork-carton-types" value={d.cartonType} onChange={set('cartonType')} placeholder="e.g. RTI" />
            <datalist id="artwork-carton-types">
              {['RTI', 'STE', 'Crash Lock', 'Lock Bottom', 'Sleeve', 'Tray'].map((t) => <option key={t} value={t} />)}
            </datalist>
          </label>
        </div>
        <label className="label !normal-case relative sm:col-span-2">Job name *
          <input className="input" value={d.jobName}
                 onChange={(e) => { set('jobName')(e); setJobOpen(true); }}
                 onFocus={() => setJobOpen(true)}
                 onBlur={() => setTimeout(() => setJobOpen(false), 150)}
                 placeholder="Type to pick from Product Master, or type a new name" />
          {jobOpen && (productHits.data ?? []).length > 0 && (
            <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded-md border border-slate-200 bg-white text-left font-normal shadow-lg">
              {(productHits.data ?? []).map((pr) => {
                const cl = pr.client ? (clients.data ?? []).find((c) => c.id === pr.client) : undefined;
                return (
                  <button key={pr.id} type="button" className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-100"
                          onMouseDown={(e) => { e.preventDefault(); pickProduct(pr); }}>
                    <div className="font-medium text-slate-800">{pr.name}</div>
                    <div className="text-[11px] text-slate-500">{pr.id}{cl ? ` · ${cl.name}` : ''}{pr.dieNumber ? ` · Die ${pr.dieNumber}` : ''}</div>
                  </button>
                );
              })}
            </div>
          )}
          <span className="text-[10px] font-normal text-slate-500">Suggestions from Product Master; anything not picked is kept as typed.</span>
        </label>
        <div className="sm:col-span-2 grid grid-cols-[1fr_1fr_1fr_5rem] gap-2">
          <label className="label !normal-case">Carton L (mm)
            <input className="input" value={d.cartonL} placeholder="or 68x53x67"
                   onChange={(e) => {
                     // A whole size typed or pasted ("68 x 53 x 67") fills all three.
                     const m = /(\d+(?:\.\d+)?)\s*[xX×*]\s*(\d+(?:\.\d+)?)(?:\s*[xX×*]\s*(\d+(?:\.\d+)?))?/.exec(e.target.value);
                     if (m) setD((x) => ({ ...x, cartonL: m[1]!, cartonW: m[2]!, cartonH: m[3] ?? x.cartonH }));
                     else setNum('cartonL')(e);
                   }} />
          </label>
          <label className="label !normal-case">W (mm)
            <input className="input" inputMode="decimal" value={d.cartonW} onChange={setNum('cartonW')} />
          </label>
          <label className="label !normal-case">H (mm)
            <input className="input" inputMode="decimal" value={d.cartonH} onChange={setNum('cartonH')} />
          </label>
          <label className="label !normal-case">Ups
            <input className="input" inputMode="numeric" value={d.ups} onChange={setNum('ups')} />
          </label>
          {d.oldCarton && !d.cartonL && <span className="col-span-4 -mt-1 text-[10px] text-slate-500">was: {d.oldCarton}</span>}
        </div>
        <DieSuggestions d={d} onPick={(h) => setD((x) => ({
          ...x, dieNo: h.dieNo, cartonType: x.cartonType || h.cartonType || '', ups: x.ups || num(h.ups), cutL: num(h.cutL) || x.cutL, cutW: num(h.cutW) || x.cutW,
        }))} />
        <div className="sm:col-span-2 grid grid-cols-2 gap-2">
          <label className="label !normal-case">Sheet cut size L (mm)
            <input className="input" inputMode="decimal" value={d.cutL} onChange={setNum('cutL')} />
          </label>
          <label className="label !normal-case">Sheet cut size W (mm)
            <input className="input" inputMode="decimal" value={d.cutW} onChange={setNum('cutW')} />
          </label>
          {d.oldSheet && !d.cutL && <span className="col-span-2 -mt-1 text-[10px] text-slate-500">was: {d.oldSheet} — enter it in mm, or leave blank to keep it</span>}
        </div>
        <label className="label !normal-case">Status
          <select className="input" value={d.status} onChange={set('status')}>
            {[...new Set([d.status, ...statuses])].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="label !normal-case">Acx (designer)
          <select className="input" value={d.designerId} onChange={set('designerId')}>
            <option value="">—</option>
            {designers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </label>
        <label className="label !normal-case sm:col-span-2">Remark
          <textarea className="input resize-none" rows={2} value={d.remark} onChange={set('remark')} />
        </label>
        <div className="sm:col-span-2 text-xs text-slate-600">
          PO: {d.poId ? <><span className="font-mono">{d.poLabel || d.poId}</span>{' '}
            <button type="button" className="text-red-600 hover:underline" onClick={() => setD((x) => ({ ...x, poId: '', poLabel: '' }))}>remove</button></>
            : <span className="text-slate-400">none yet — use "Link PO" in the list when it comes</span>}
        </div>
      </div>
      {save.isError && <div className="mt-3 text-xs text-red-600">{friendly(save.error)}</div>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!ok || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : id ? 'Save' : 'Book artwork'}
        </button>
      </div>
    </Modal>
  );
}

function LinkPoModal({ artwork, onClose, onDone }: { artwork: Artwork; onClose: () => void; onDone: () => void }) {
  const [q, setQ] = useState(artwork.clientCode || artwork.jobName.split(' ')[0] || '');
  const [term, setTerm] = useState(q);
  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const hits = useQuery({
    enabled: term.length >= 2,
    queryKey: ['artwork-po-search', term],
    queryFn: () => api<{ rows: PoHit[] }>(`/api/artworks/po-search?q=${encodeURIComponent(term)}`),
  });
  const link = useMutation({
    mutationFn: (poId: string | null) => api(`/api/artworks/${artwork.id}/po`, { method: 'PUT', body: JSON.stringify({ poId }) }),
    onSuccess: onDone,
  });
  return (
    <Modal title={`Link PO — artwork ${artwork.artworkNo}`} onClose={onClose} wide>
      <p className="-mt-1 mb-2 text-xs text-slate-500">{artwork.clientName} · {artwork.jobName}</p>
      <input className="input mb-2" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="PO number, job name, client or client code…" />
      <div className="max-h-80 overflow-y-auto rounded border border-slate-100">
        {term.length < 2 && <div className="p-3 text-xs text-slate-400">Type at least 2 letters.</div>}
        {hits.isLoading && <div className="p-3 text-xs text-slate-500">Searching…</div>}
        {hits.data && hits.data.rows.length === 0 && <div className="p-3 text-xs text-slate-500">No PO matches.</div>}
        {hits.data?.rows.map((h) => {
          const taken = !!h.artworkNo && h.poId !== artwork.poId;
          return (
            <button key={h.poId} type="button" disabled={taken || link.isPending} onClick={() => link.mutate(h.poId)}
                    className="block w-full border-b border-slate-100 px-3 py-2 text-left hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-mono text-brand">{h.poNumber || h.poId}</span>
                <span className="text-slate-800">{h.jobName}</span>
                {h.poId === artwork.poId && <span className="text-[10px] font-semibold text-emerald-700">linked now</span>}
              </div>
              <div className="text-[11px] text-slate-500">
                {h.client} · {h.poDate} · {h.poId}{h.materialNo ? ` · code ${h.materialNo}` : ''}{h.orderType ? ` · ${h.orderType}` : ''}
                {taken && <span className="ml-1 text-amber-700">— already has artwork {h.artworkNo}</span>}
              </div>
            </button>
          );
        })}
      </div>
      {link.isError && <div className="mt-2 text-xs text-red-600">{friendly(link.error)}</div>}
      <div className="mt-3 flex justify-between">
        {artwork.poId
          ? <button className="text-xs text-red-600 hover:underline" onClick={() => link.mutate(null)}>Unlink PO {artwork.poNumber || artwork.poId}</button>
          : <span />}
        <Link to="/queue/design" className="text-xs text-slate-500 hover:underline">Design Queue →</Link>
      </div>
    </Modal>
  );
}

/** Dies in the Die Master that fit the carton (and ups): tap one to fill Die No. and the cut size. */
function DieSuggestions({ d, onPick }: { d: Draft; onPick: (h: DieHit) => void }) {
  const [key, setKey] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setKey(d.cartonL && d.cartonW ? `${d.cartonL}|${d.cartonW}|${d.cartonH}|${d.ups}` : ''), 350);
    return () => clearTimeout(t);
  }, [d.cartonL, d.cartonW, d.cartonH, d.ups]);
  const q = useQuery({
    enabled: !!key,
    queryKey: ['artwork-die-suggest', key],
    queryFn: () => {
      const [l, w, h, ups] = key.split('|');
      const sp = new URLSearchParams({ l: l!, w: w! });
      if (h) sp.set('h', h);
      if (ups) sp.set('ups', ups);
      return api<{ rows: DieHit[] }>(`/api/artworks/die-suggest?${sp}`);
    },
    staleTime: 60_000,
  });
  if (!key) return <p className="sm:col-span-2 -mt-1 text-[11px] text-slate-400">Enter the carton size — as in the Job Orders search — to see the dies used for it.</p>;
  return (
    <div className="sm:col-span-2 rounded border border-slate-200 bg-slate-50 p-2">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        Dies for {d.cartonL} × {d.cartonW}{d.cartonH ? ` × ${d.cartonH}` : ''} — tap to use
      </div>
      {q.isLoading && <div className="text-xs text-slate-500">Searching Die Master and Product Master…</div>}
      {q.data && q.data.rows.length === 0 && <div className="text-xs text-slate-500">No die found for this size — a new die; type its Die No. and cut size.</div>}
      <div className="flex flex-wrap gap-1.5">
        {q.data?.rows.map((h) => {
          const picked = d.dieNo === h.dieNo;
          const otherUps = !!d.ups && !h.upsMatch;
          return (
            <button key={h.dieNo} type="button" onClick={() => onPick(h)}
                    className={`max-w-full rounded border px-2 py-1 text-left text-xs ${picked ? 'border-brand bg-brand text-white' : otherUps ? 'border-slate-200 bg-white/60 text-slate-500 hover:border-brand' : 'border-slate-300 bg-white hover:border-brand'}`}>
              <div className="font-semibold">Die {h.dieNo}{h.ups ? ` · ${h.ups} ups` : ''}{otherUps ? ' (other ups)' : ''}</div>
              <div className={picked ? 'text-white/80' : 'text-slate-500'}>
                cut {h.cutL || '?'} × {h.cutW || '?'} · {h.l}×{h.w}{h.h ? `×${h.h}` : ''}{h.designNo ? ` · design ${h.designNo}` : ''}
              </div>
              <div className={`truncate ${picked ? 'text-white/70' : 'text-slate-400'}`}>
                {h.source}{h.productName ? `: ${h.productName}` : ''}{h.client ? ` · ${h.client}` : ''}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
